import {
    FocusEvent,
    KeyboardEvent,
    useCallback,
    useEffect,
    useRef,
} from 'react';

import {
    GRID_CELL_SELECTOR,
    NavKey,
    isLastGridCell,
    isTextCaretMove,
    resolveNavigationTarget,
    rowCells,
} from './gridNavigation';

/** Roving-tabindex keyboard navigation for `RowEditor`'s grid via direct DOM mutation, so it causes zero React re-renders. */
export function useGridRovingFocus(
    rowCount: number,
    colCount: number,
    onAddRow?: () => void,
) {
    const gridRef = useRef<HTMLDivElement>(null);
    const activeCellRef = useRef<HTMLElement | null>(null);
    const focusNewRowRef = useRef(false);

    // rowCount/colCount are lengths, not arrays, so this effect doesn't re-run on every keystroke.
    useEffect(() => {
        const container = gridRef.current;
        if (!container) return;
        const cells = Array.from(
            container.querySelectorAll<HTMLElement>(GRID_CELL_SELECTOR),
        );
        if (cells.length === 0) {
            activeCellRef.current = null;
            return;
        }
        if (
            activeCellRef.current &&
            container.contains(activeCellRef.current)
        ) {
            return;
        }
        cells.forEach((c) => {
            c.tabIndex = -1;
        });
        cells[0].tabIndex = 0;
        activeCellRef.current = cells[0];
    }, [rowCount, colCount]);

    // After Enter adds a row, focus its first cell once it has rendered.
    useEffect(() => {
        if (!focusNewRowRef.current) return;
        focusNewRowRef.current = false;
        const rows = gridRef.current?.querySelectorAll('tbody tr');
        const lastRow = rows?.[rows.length - 1];
        if (lastRow) rowCells(lastRow)[0]?.focus();
    }, [rowCount]);

    const handleGridFocus = useCallback((e: FocusEvent<HTMLDivElement>) => {
        const target = e.target;
        if (
            !(target instanceof HTMLElement) ||
            !target.matches(GRID_CELL_SELECTOR)
        ) {
            return;
        }
        const prev = activeCellRef.current;
        if (prev && prev !== target) prev.tabIndex = -1;
        target.tabIndex = 0;
        activeCellRef.current = target;
    }, []);

    const handleGridKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            const target = e.target;
            if (
                !(target instanceof HTMLElement) ||
                !target.matches(GRID_CELL_SELECTOR)
            ) {
                return;
            }

            if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                // Let a mid-text-edit caret move natively instead of navigating —
                // only jump cells once the caret is at the boundary matching the
                // arrow direction.
                if (isTextCaretMove(target, e.key)) return;
            }

            const navKey: NavKey | null =
                e.key === 'Tab'
                    ? e.shiftKey
                        ? 'ShiftTab'
                        : 'Tab'
                    : e.key === 'ArrowUp' ||
                        e.key === 'ArrowDown' ||
                        e.key === 'ArrowLeft' ||
                        e.key === 'ArrowRight' ||
                        e.key === 'Enter'
                      ? e.key
                      : null;
            if (!navKey) return;

            // Every other key (typing, Backspace, Home/End, ...) falls through
            // untouched so it keeps editing the focused cell.
            e.preventDefault();
            const next = resolveNavigationTarget(target, navKey);
            if (next) {
                next.focus();
            } else if (
                navKey === 'Enter' &&
                onAddRow &&
                isLastGridCell(target)
            ) {
                focusNewRowRef.current = true;
                onAddRow();
            }
        },
        [onAddRow],
    );

    return { gridRef, handleGridFocus, handleGridKeyDown };
}
